import { DOCUMENT } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import {
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  InputSignal,
  isDevMode,
  OnInit,
  Signal,
  signal,
  viewChild,
  WritableSignal,
  ChangeDetectionStrategy
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, FormControl, FormGroup, FormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { Router } from '@angular/router';
import { PromptService, ToastMessageService, AppInsightsService } from '@k12/app-services';
import { LoadingSpinnerService } from '@k12/loading';
import {
  ComponentType,
  findDiscriminatorInput,
  InputDataType,
  NextPrompt,
  ProgramReview,
  ProgramTypes,
  Prompt,
  PromptComponent,
  PromptInput,
  PromptInputOption,
  ProviderType,
  SubmitPrompt,
  SubmitPromptResponse,
  ToastMessageType
} from '@k12/models';
import { OrderByPipe } from '@k12/pipes';
import { errorToastValue } from '@k12/utils';
import {
  feinValidator,
  householdSizeValidator,
  numberValidator,
  numericOrDecimalValidator,
  phoneValidator,
  requiredEmailValidator,
  ssnLast4Validator,
  ssnValidator,
  urlValidator,
  zipCodeValidator
} from '@k12/validators';
import { combineLatest, startWith } from 'rxjs';
import { PromptRendererComponent } from '../prompt-renderer/prompt-renderer.component';
import { SchoolYearLabelsService } from './school-year-labels.service';
import { hasSchoolYearTokens, resolveSchoolYearTokens } from './school-year-tokens';

// RadioFormField components use N independent boolean FormControls (one per option, same
// shape as CheckboxFormField) rather than one shared control, so "must select one option when
// required" can't be expressed with Validators.required/requiredTrue on an individual control --
// it has to be evaluated across the whole group.
export function atLeastOneSelectedValidator(): ValidatorFn {
  return (group: AbstractControl): ValidationErrors | null => {
    const formGroup = group as FormGroup;
    const hasSelection = Object.values(formGroup.controls).some((control) => control.value === true);
    return hasSelection ? null : { atLeastOneRequired: true };
  };
}

// The value stored in a FormControl is always a string, number, boolean, or undefined.
// Datepickers hold Date | null, but the enrollment API expects an ISO string, so the control value is converted to a string before submit.
type StoredPromptValue = string | number | boolean | undefined;

/** The review the user left when they clicked Edit on a section, so Cancel and Save can put them back on it. */
interface ReviewReturn {
  prompt: Prompt | NextPrompt;
  review: ProgramReview;
}

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [FormsModule, MatButtonModule, OrderByPipe, PromptRendererComponent],
  selector: 'lib-enrollment-renderer',
  styleUrl: './enrollment-renderer.component.scss',
  templateUrl: './enrollment-renderer.component.html'
})
export class EnrollmentRendererComponent implements OnInit {
  public promptRendererComponent: Signal<PromptRendererComponent | undefined> = viewChild(PromptRendererComponent);

  public formGroups: Record<string, FormGroup> = {};
  public InputDataType: typeof InputDataType = InputDataType;
  public isBackDisabled: WritableSignal<boolean> = signal(true);
  public isPromptValid: WritableSignal<boolean> = signal(false);
  public isReviewPrompt: WritableSignal<boolean> = signal(false);
  public promptData: WritableSignal<Prompt | NextPrompt> = signal({} as unknown as Prompt);
  public reviewComponentData: WritableSignal<ProgramReview> = signal(<ProgramReview>{});
  public sessionId: WritableSignal<string> = signal('');

  public appUrl: InputSignal<string | undefined> = input();
  public isIneligible: InputSignal<boolean> = input(false);
  public initialSessionId: InputSignal<string> = input('');
  public postSubmitPath: InputSignal<string | undefined> = input();
  public programToken: InputSignal<string> = input('');
  public programType: InputSignal<ProgramTypes> = input.required();

  private readonly appInsightsService = inject(AppInsightsService);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);
  private readonly document: Document = inject(DOCUMENT);
  private readonly promptService: PromptService = inject(PromptService);
  private readonly router: Router = inject(Router);
  private readonly spinnerService: LoadingSpinnerService = inject(LoadingSpinnerService);
  private readonly toastMessageService: ToastMessageService = inject(ToastMessageService);
  private readonly schoolYearLabels = inject(SchoolYearLabelsService);
  private promptVersion = 0;
  private reviewVersion = 0;

  // Set only while the user is editing a section they reached from the review screen.
  private readonly reviewReturn: WritableSignal<ReviewReturn | null> = signal(null);

  /** Swaps Back/Next for Cancel/Save, since an edit from review returns to review rather than walking the wizard. */
  public readonly isEditingFromReview: Signal<boolean> = computed(() => this.reviewReturn() !== null);

  /**
   * Post-submit landing routes that read the session id as their support reference and/or retry key.
   * Every completion page keyed off the submitted session needs its id appended the same way.
   */
  private static readonly SessionIdCompletionPaths: readonly string[] = [
    '/enrollment-complete',
    '/add-student-complete'
  ];

  constructor() {
    effect(() => {
      if (this.promptData()?.components && this.promptData().components?.length > 0) {
        if (this.promptData().parentPromptToken) {
          this.isBackDisabled.set(!this.promptData().parentPromptToken && this.promptData().parentPromptToken! !== '');
        } else {
          this.isBackDisabled.set(this.promptData().parentPromptTokens!.length === 0);
        }
        this.createFormGroup();
      }
    });
  }

  public ngOnInit(): void {
    const existingSessionId = this.initialSessionId();
    if (existingSessionId) {
      this.sessionId.set(existingSessionId);
      this.loadExistingSession(existingSessionId);
      return;
    }
    if (this.programToken()) {
      this.getPromptData();
      return;
    }
    // programToken is no longer input.required() now that sessionId can bootstrap instead, so
    // neither being supplied would otherwise render an empty shell with no indication of why.
    if (isDevMode()) {
      throw new Error('EnrollmentRendererComponent requires either initialSessionId or programToken.');
    }
  }

  private setPrompt(prompt: Prompt | NextPrompt): void {
    const version = ++this.promptVersion;
    this.withSchoolYears(prompt, (resolved) => {
      if (version === this.promptVersion) {
        this.promptData.set(resolved);
      }
    });
  }

  private setReview(review: ProgramReview): void {
    const version = ++this.reviewVersion;
    this.withSchoolYears(review, (resolved) => {
      if (version === this.reviewVersion) {
        this.reviewComponentData.set(resolved);
      }
    });
  }

  private withSchoolYears<T>(value: T, apply: (resolved: T) => void): void {
    if (!hasSchoolYearTokens(value)) {
      apply(value);
      return;
    }

    this.schoolYearLabels
      .labels()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((labels) => apply(resolveSchoolYearTokens(value, labels)));
  }

  public back(): void {
    this.promptService.getPreviousPrompt(this.sessionId()).subscribe({
      next: (promptResponse: SubmitPromptResponse) => {
        if (promptResponse.prompt) {
          promptResponse.prompt.promptDataDictionary = promptResponse.promptDataDictionary!;
          this.setPrompt(promptResponse.prompt);
        }
      },
      error: (error: unknown) => this.appInsightsService.log(error instanceof Error ? error : new Error(String(error)))
    });
  }

  /**
   * Abandons an edit reached from review and restores the review exactly as the user left it.
   * Nothing is submitted, and opening a section for edit leaves the session cursor on the review
   * prompt, so nothing the server holds has changed since the snapshot was taken.
   */
  public cancelEdit(): void {
    const snapshot = this.reviewReturn();
    if (snapshot) {
      this.returnToReview(snapshot);
    }
  }

  /**
   * Persists an edit reached from review, then returns to review with the saved answers. Review is
   * re-fetched because the snapshot predates the edit. If the edit flipped a decision, the server
   * has cleared the abandoned branch and the new branch has unanswered prompts, so the user walks
   * through those instead of returning to a review that would be missing them.
   */
  public save(): void {
    const snapshot = this.reviewReturn();
    if (!snapshot) {
      return;
    }

    this.spinnerService.showSpinner();
    this.promptService.submitPrompt(this.sessionId(), this.formatSubmitPromptData()).subscribe({
      next: (promptResponse: SubmitPromptResponse) => {
        if (promptResponse.branchChanged) {
          this.showNextPrompt(promptResponse);
          return;
        }
        // The server's cursor has moved past the edited section, but the user is headed back to the
        // review they came from, so only its data is fetched.
        this.promptService.getReviewPrompt(this.sessionId()).subscribe({
          next: (review: ProgramReview) => {
            this.returnToReview({ prompt: snapshot.prompt, review });
            this.spinnerService.hideSpinner();
          },
          error: (err: HttpErrorResponse) => {
            this.spinnerService.hideSpinner();
            this.toastMessageService.showToast(ToastMessageType.Error, errorToastValue(err));
          }
        });
      },
      error: (error: HttpErrorResponse) => {
        this.spinnerService.hideSpinner();
        this.toastMessageService.showToast(ToastMessageType.Error, errorToastValue(error));
      }
    });
  }

  private returnToReview(snapshot: ReviewReturn): void {
    this.promptRendererComponent()?.resetChildContainers();
    this.reviewReturn.set(null);
    this.setReview(snapshot.review);
    this.setPrompt(snapshot.prompt);
  }

  public next(): void {
    const submitPromptData: SubmitPrompt = this.formatSubmitPromptData();

    this.spinnerService.showSpinner();
    this.promptService.submitPrompt(this.sessionId(), submitPromptData).subscribe({
      next: (promptResponse: SubmitPromptResponse) => this.showNextPrompt(promptResponse),
      error: (error: HttpErrorResponse) => {
        this.spinnerService.hideSpinner();
        this.toastMessageService.showToast(ToastMessageType.Error, errorToastValue(error));
      }
    });
  }

  /** Renders the prompt the server moved the session to after a submit, and hides the spinner. */
  private showNextPrompt(promptResponse: SubmitPromptResponse): void {
    // Moving forward retires any review snapshot: the user is walking the wizard again rather than
    // sitting on an edit detour.
    this.reviewReturn.set(null);
    // Keep the spinner up through getReviewPrompt so the previous interactive prompt cannot
    // flash after submit returns and before the read-only review is ready to render.
    if (this.hasReviewComponent(promptResponse.nextPrompt.components)) {
      this.promptService.getReviewPrompt(this.sessionId()).subscribe({
        next: (reviewResponse) => {
          this.promptRendererComponent()?.resetChildContainers();
          this.setReview(reviewResponse);
          this.setPrompt(promptResponse.nextPrompt);
          this.spinnerService.hideSpinner();
        },
        error: (err: HttpErrorResponse) => {
          this.spinnerService.hideSpinner();
          this.toastMessageService.showToast(ToastMessageType.Error, errorToastValue(err));
        }
      });
    } else {
      this.promptRendererComponent()?.resetChildContainers();
      this.setPrompt(promptResponse.nextPrompt);
      this.spinnerService.hideSpinner();
    }
  }

  public submitEnrollment(): void {
    this.spinnerService.showSpinner();
    this.promptService.submitEnrollment(this.sessionId()).subscribe({
      next: () => {
        this.spinnerService.hideSpinner();
        this.navigateAfterSubmit();
      },
      error: (error: HttpErrorResponse) => {
        this.spinnerService.hideSpinner();
        this.toastMessageService.showToast(ToastMessageType.Error, errorToastValue(error));
      }
    });
  }

  /**
   * Router navigation, not a location assign: a full page load re-bootstraps the app, which re-runs
   * TokenValidationService and can call account/assign-new-user-security while the registration saga
   * is still provisioning. It also discards the MSAL in-memory state for no benefit.
   */
  private navigateAfterSubmit(): void {
    const postSubmitPath = this.postSubmitPath();
    if (postSubmitPath) {
      void this.router.navigateByUrl(postSubmitPath);
      return;
    }

    const target = this.appUrl();
    if (!target) {
      return;
    }

    const url = new URL(target, this.document.location.origin);

    // Only this component knows the session id -- the wrapper built this URL before the session
    // existed. Set rather than append, so a stale one from the route cannot survive.
    const sessionId = this.sessionId();
    const isSessionIdCompletionPath = EnrollmentRendererComponent.SessionIdCompletionPaths.some((path) =>
      url.pathname.endsWith(path)
    );
    const isStudentCompletionPath = url.pathname.includes('/student/')

    if (sessionId && isSessionIdCompletionPath) {
      url.searchParams.set('sessionId', sessionId);
    }

    if (url.origin !== this.document.location.origin) {
      this.document.location.assign(url.toString());
      return;
    }

    let fullUrlPath
    if (isSessionIdCompletionPath) {
      fullUrlPath = `${url.pathname}${url.search}${url.hash}`;
    } else if (isStudentCompletionPath) {
      fullUrlPath = `${url.pathname}`;
    } else {
      fullUrlPath = `${url.pathname}mypage`;
    }

    void this.router.navigateByUrl(fullUrlPath);
  }

  public editPrompt(event: { id: string; token: string }): void {
    this.spinnerService.showSpinner();
    this.promptService.getSessionPromptByToken(event.id, event.token).subscribe({
      next: (response) => {
        this.spinnerService.hideSpinner();
        // Captured before reviewComponentData is cleared, so Cancel from the edit screen can put the
        // review back exactly as the user left it.
        if (this.hasReviewComponent(this.promptData().components)) {
          this.reviewReturn.set({ prompt: this.promptData(), review: this.reviewComponentData() });
        }
        this.setReview(<ProgramReview>{});
        response.prompt.promptDataDictionary = response.promptDataDictionary!;
        this.setPrompt(response.prompt);
      },
      error: (error: HttpErrorResponse) => {
        this.spinnerService.hideSpinner();
        this.toastMessageService.showToast(ToastMessageType.Error, errorToastValue(error));
      }
    });
  }

  public agreementSelectOutput(event: { formName: string; control: Record<string, unknown> }): void {
    this.formGroups[event.formName].patchValue(event.control);
  }

  // Generic on purpose: CheckboxFormField emits a single-key control dict (one option
  // independently toggled); RadioFormField emits a multi-key control dict covering every
  // sibling option (the clicked one true, the rest false) to enforce mutual exclusion. Both
  // shapes are just "apply every entry in the control dict", so one handler covers both.
  public checkboxSelectOutput(event: { formName: string; control: Record<string, unknown> }): void {
    Object.entries(event.control).forEach(([inputId, value]) => {
      this.formGroups[event.formName]?.controls[inputId]?.setValue(value);
    });
  }

  public dnpeComponentOutput(event: { formName: string; control: Record<string, unknown> }): void {
    this.formGroups[event.formName].patchValue(event.control);
  }

  public decisionComponentOutput(event: { formName: string; control: Record<string, unknown> }): void {
    this.formGroups[event.formName].patchValue(event.control);
  }

  public fileUploadOutput(event: { formName: string; control: Record<string, unknown> }): void {
    this.formGroups[event.formName].patchValue(event.control);
    this.formGroups[event.formName].updateValueAndValidity();
  }

  public buttonSelectOutput(event: {
    sub: PromptInputOption;
    subSelectInputId: string;
    parentId: string;
  }): void {
    const formGroup = this.formGroups[event.parentId];
    const control = formGroup?.controls[event.subSelectInputId];
    if (!control) {
      return;
    }

    const parentInput = this.promptData().components
      .find((component) => component.id === event.parentId)
      ?.inputs?.find((input) => input.id === event.subSelectInputId);
    const isSingleSelect = parentInput?.value === 'therapy';

    if (isSingleSelect) {
      control.setValue(event.sub.selected ? [event.sub.value] : []);
    } else {
      const currentValue: string[] = Array.isArray(control.value) ? control.value : [];
      if (event.sub.selected) {
        control.setValue([...currentValue.filter((item: string) => item !== event.sub.value), event.sub.value]);
      } else {
        control.setValue(currentValue.filter((item: string) => item !== event.sub.value));
      }
    }

    Object.keys(this.formGroups).forEach((key: string) => {
      this.formGroups[key].updateValueAndValidity();
    });
  }

  public selectSubSelectCheckboxOutput(event: { parentId: string; value: PromptInput }): void {
    const formGroup = this.formGroups[event.parentId];
    if (!formGroup || !event.value.id) {
      return;
    }

    const component = this.promptData().components.find((c) => c.id === event.parentId);
    const path = this.subSelectPathControl(component);

    // SelectSubSelect parents are mutually exclusive: clear sibling option controls. The
    // discriminator survives the sweep because it is the branch answer, not a sub-option bucket;
    // the legacy placeholder does not, since it exists only to gate the nothing-selected state.
    Object.keys(formGroup.controls).forEach((controlName) => {
      const keep = controlName === event.value.id || (path.isDiscriminator && controlName === path.id);
      if (!keep) {
        formGroup.removeControl(controlName);
      }
    });

    if (event.value.selected) {
      if (path.isDiscriminator) {
        formGroup.controls[path.id]?.setValue(event.value.value ?? '');
      }
      formGroup.addControl(
        event.value.id,
        new FormControl([], event.value.value !== ProviderType.Transportation ? Validators.required : undefined));
    } else {
      formGroup.removeControl(event.value.id);

      // Emptying the path control is what keeps the form invalid when no path is selected.
      if (path.isDiscriminator) {
        formGroup.controls[path.id]?.setValue('');
      } else {
        formGroup.addControl(path.id, new FormControl([], Validators.required));
      }
      formGroup.markAsPristine();
    }

    formGroup.updateValueAndValidity();
  }

  /**
   * The control holding a SelectSubSelect component's chosen path. Normally the server-owned
   * discriminator input, whose answer the branch conditions evaluate. Legacy components authored
   * before the discriminator existed fall back to a component-keyed placeholder that gates validity
   * but is never submitted -- an answer row keyed by a component id would violate PromptData's
   * input foreign key.
   */
  private subSelectPathControl(component?: PromptComponent): { id: string; isDiscriminator: boolean } {
    const discriminatorId = findDiscriminatorInput(component)?.id;
    return discriminatorId
      ? { id: discriminatorId, isDiscriminator: true }
      : { id: component?.id ?? '', isDiscriminator: false };
  }

  private createFormGroup(): void {
    this.formGroups = {};

    // Components and inputs coming from the enrollment API always carry server-assigned GUIDs;
    // form groups are keyed by component id and controls by input id.
    this.promptData().components.forEach((component: PromptComponent) => {
      const componentId = component.id!;
      this.formGroups[componentId] = new FormGroup({});

      if (component.inputs && component.inputs?.length > 0) {
        // SelectSubSelect sub-option controls are added dynamically when a parent option is checked.
        // Only the path control exists up front; its required validator keeps the form invalid until
        // a path is picked.
        if (component.componentType === ComponentType.SelectSubSelect) {
          this.hydrateSelectSubSelectControls(component, this.formGroups[componentId]);
          return;
        }

        component.inputs.forEach((input: PromptInput) => {
          const inputId = input.id!;
          const isDateInput = this.isDateInput(input.dataType);
          const rawStoredValue = this.returnFormInputValue(
            inputId,
            component.componentType === ComponentType.TermsAndConditions ? false : ''
          );
          const storedValue = this.coerceStoredControlValue(component.componentType, rawStoredValue);
          // Datepickers hold Date | null. Keep that shape so MatDatepickerInput and submit agree.
          const storedDateValue = typeof storedValue === 'string' ? storedValue : undefined;
          const previousValue = isDateInput
            ? this.parseDateControlValue(storedDateValue)
            : storedValue;

          this.formGroups[componentId].addControl(inputId, new FormControl(previousValue));

          if (previousValue && this.returnFormInputValue(inputId)) {
            this.formGroups[componentId].controls[inputId].markAsDirty();
          }

          if (input.isRequired) {
            if (
              component.componentType === ComponentType.TermsAndConditions ||
              component.componentType === ComponentType.CheckboxFormField
            ) {
              this.formGroups[componentId].controls[inputId].addValidators(Validators.requiredTrue);
            } else if (component.componentType !== ComponentType.RadioFormField) {
              // RadioFormField: forcing every option's control to true/requiredTrue would make the
              // group unsatisfiable. "Must select one" is enforced once per component below instead.
              this.formGroups[componentId].controls[inputId].addValidators(Validators.required);
            }
          }

          if (input.max) {
            this.formGroups[componentId].controls[inputId].addValidators(Validators.maxLength(Number(input.max)));
          }

          switch (input.dataType) {
            case InputDataType.Email:
              this.formGroups[componentId].controls[inputId].addValidators(requiredEmailValidator());
              break;
            case InputDataType.Phone:
              this.formGroups[componentId].controls[inputId].addValidators(phoneValidator());
              break;
            case InputDataType.Url:
              this.formGroups[componentId].controls[inputId].addValidators(urlValidator());
              break;
            case InputDataType.SSN4:
              this.formGroups[componentId].controls[inputId].addValidators(ssnLast4Validator());
              break;
            case InputDataType.SSN9:
              this.formGroups[componentId].controls[inputId].addValidators(ssnValidator());
              break;
            case InputDataType.ZipCode:
              this.formGroups[componentId].controls[inputId].addValidators(zipCodeValidator());
              break;
            case InputDataType.EIN:
              this.formGroups[componentId].controls[inputId].addValidators(feinValidator());
              break;
            case InputDataType.IdNumber:
              this.formGroups[componentId].controls[inputId].addValidators(numberValidator());
              break;
            case InputDataType.HHSize:
              this.formGroups[componentId].controls[inputId].addValidators(householdSizeValidator());
              break;
            case InputDataType.Number:
              if (input.min) {
                this.formGroups[componentId].controls[inputId].addValidators(Validators.min(Number(input.min)));
              }
              break;
            case InputDataType.HHIncome:
              this.formGroups[componentId].controls[inputId].addValidators(numericOrDecimalValidator());
              break;
          }
        });

        if (component.componentType === ComponentType.RadioFormField && component.inputs.some((i) => i.isRequired)) {
          this.formGroups[componentId].addValidators(atLeastOneSelectedValidator());
          this.formGroups[componentId].updateValueAndValidity();
        }
      }
    });

    // Update the validity of all controls in the form groups to ensure that any changes
    // to validators are reflected in the form's state.
    Object.values(this.formGroups).forEach((formGroup) => {
      Object.values(formGroup.controls).forEach((control) => {
        control.updateValueAndValidity();
      });
    });

    this.subscribeFormGroup();
  }

  private formatSubmitPromptData(): SubmitPrompt {
    const submitPrompt: SubmitPrompt = {
      enrollmentSessionId: this.sessionId(),
      promptToken: this.promptData().token,
      inputValues: []
    };

    for (const formGroup of Object.values(this.formGroups)) {
      for (const [inputId, value] of Object.entries(formGroup.value)) {
        if (Array.isArray(value) && value.length > 0) {
          value.forEach((arrayValue: string) => {
            submitPrompt.inputValues.push({ inputId, value: arrayValue });
          });
        } else if (value instanceof Date && !Number.isNaN(value.getTime())) {
          // mat-datepicker writes a Date. JSON would turn that into an ISO string, but this
          // loop previously kept only string | number | boolean, so Date of Birth never left
          // the client and projection failed with "missing: Date of Birth".
          submitPrompt.inputValues.push({ inputId, value: this.formatDateControlValue(value) });
        } else if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
          submitPrompt.inputValues.push({ inputId, value: value.toString() });
        }
      }
    }
    return submitPrompt;
  }

  /**
   * Hydrates the form group for a SelectSubSelect component with the stored path and sub-option values.
   * This method is called during form group creation to ensure that the form group reflects the
   * previously selected values for the component.
   *
   * @param component - The SelectSubSelect component whose form group is being hydrated.
   * @param formGroup - The FormGroup associated with the SelectSubSelect component.
   */
  private hydrateSelectSubSelectControls(component: PromptComponent, formGroup: FormGroup): void {
    const path = this.subSelectPathControl(component);
    this.addSelectSubSelectPathControl(formGroup, path.id, path.isDiscriminator);

    const selectedInput = this.findRestoredSelectSubSelectInput(component, path.id, path.isDiscriminator);
    this.resetSelectSubSelectUiState(component, selectedInput?.id);

    if (!selectedInput?.id) {
      return;
    }

    const restoredSubValues = this.toStringArray(this.returnFormInputValue(selectedInput.id));
    this.addSelectSubSelectOptionsControl(formGroup, selectedInput, restoredSubValues);
    this.applyRestoredOptionSelections(selectedInput, restoredSubValues);

    if (!path.isDiscriminator) {
      formGroup.controls[path.id]?.setValue(selectedInput.id);
    }
  }

  /**
   * Adds the path control for a SelectSubSelect component to the form group.
   * The path control is required and holds the selected path value for the component.
   *
   * @param formGroup - The FormGroup associated with the SelectSubSelect component.
   * @param pathId - The ID of the path control to be added.
   * @param isDiscriminator - A boolean indicating whether the path control is a discriminator.
   */
  private addSelectSubSelectPathControl(formGroup: FormGroup, pathId: string, isDiscriminator: boolean): void {
    const storedPathValue = this.returnFormInputValue(pathId, '');
    formGroup.addControl(
      pathId,
      isDiscriminator
        ? new FormControl(storedPathValue ?? '', Validators.required)
        : new FormControl([], Validators.required)
    );
  }

  /**
   * Finds the restored input for a SelectSubSelect component based on the stored path value.
   * This method is used to determine which input was previously selected for the component.
   *
   * @param component - The SelectSubSelect component whose restored input is being searched for.
   * @param pathId - The ID of the path control associated with the component.
   * @param isDiscriminator - A boolean indicating whether the path control is a discriminator.
   * @returns The restored PromptInput if found, otherwise undefined.
   */
  private findRestoredSelectSubSelectInput(
    component: PromptComponent,
    pathId: string,
    isDiscriminator: boolean
  ): PromptInput | undefined {
    const storedPathValue = this.returnFormInputValue(pathId, '');

    return component.inputs.find((input) => {
      if (!input.id) {
        return false;
      }

      if (isDiscriminator) {
        return String(input.value ?? '') === String(storedPathValue ?? '');
      }

      const raw = this.returnFormInputValue(input.id);
      return raw !== undefined && raw !== null && raw !== '';
    });
  }

  /**
   * Resets the UI state of a SelectSubSelect component by deselecting all inputs and their options,
   * except for the specified selected input (if provided).
   *
   * @param component - The SelectSubSelect component whose UI state is being reset.
   * @param selectedInputId - The ID of the input that should remain selected, if any.
   */
  private resetSelectSubSelectUiState(component: PromptComponent, selectedInputId?: string): void {
    component.inputs.forEach((input) => {
      input.selected = input.id === selectedInputId;
      input.options?.forEach((option) => {
        option.selected = false;
      });
    });
  }

  /**
   * Adds the options control for a SelectSubSelect component to the form group.
   * The options control holds the selected sub-option values for the component.
   *
   * @param formGroup - The FormGroup associated with the SelectSubSelect component.
   * @param selectedInput - The PromptInput representing the selected input for the component.
   * @param restoredSubValues - An array of previously selected sub-option values for the component.
   */
  private addSelectSubSelectOptionsControl(
    formGroup: FormGroup,
    selectedInput: PromptInput,
    restoredSubValues: string[]
  ): void {
    formGroup.addControl(
      selectedInput.id!,
      new FormControl(
        restoredSubValues,
        selectedInput.value !== ProviderType.Transportation ? Validators.required : undefined
      )
    );
  }

  /**
   * Applies the restored option selections for a SelectSubSelect component.
   * This method updates the selected state of each option based on the previously selected values.
   *
   * @param selectedInput - The PromptInput representing the selected input for the component.
   * @param restoredSubValues - An array of previously selected sub-option values for the component.
   */
  private applyRestoredOptionSelections(selectedInput: PromptInput, restoredSubValues: string[]): void {
    selectedInput.options?.forEach((option) => {
      option.selected = restoredSubValues.includes(String(option.value ?? ''));
    });
  }

  /**
   * Converts a stored prompt value into an array of strings.
   * If the value is null, undefined, or an empty string, an empty array is returned.
   * If the value is a string containing commas, it is split into an array of trimmed strings.
   * Otherwise, the value is converted to a string and returned as a single-element array.
   *
   * @param value - The stored prompt value to be converted.
   * @returns An array of strings representing the stored prompt value.
   */
  private toStringArray(value: StoredPromptValue): string[] {
    if (value === null || value === undefined || value === '') {
      return [];
    }

    if (typeof value === 'string' && value.includes(',')) {
      return value
        .split(',')
        .map((item) => item.trim())
        .filter((item) => item.length > 0);
    }

    return [String(value)];
  }

  private isDateInput(dataType: string | undefined): boolean {
    return dataType === InputDataType.Date
      || dataType === InputDataType.FullDate
      || dataType === InputDataType.ShortDate;
  }

  // Local calendar date, not UTC. toISOString() would shift a US evening pick to the next day.
  private formatDateControlValue(value: Date): string {
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const day = String(value.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private parseDateControlValue(raw: string | undefined): Date | null {
    if (!raw?.trim()) {
      return null;
    }

    const isoDate = /^(\d{4})-(\d{2})-(\d{2})/.exec(raw.trim());
    if (isoDate) {
      const parsed = new Date(Number(isoDate[1]), Number(isoDate[2]) - 1, Number(isoDate[3]));
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    }

    const fallback = new Date(raw);
    return Number.isNaN(fallback.getTime()) ? null : fallback;
  }

  /**
   * Normalizes persisted control values for boolean-style input components.
   *
   * For `TermsAndConditions`, `CheckboxFormField`, and `RadioFormField`, this method
   * converts string boolean literals (`"true"` / `"false"`, case-insensitive and trimmed)
   * into actual boolean values. Any non-string value, unsupported component type, or
   * unrecognized string is returned unchanged.
   *
   * @param componentType - The component type used to determine whether boolean coercion should be applied.
   * @param storedValue - The raw value retrieved from storage for the prompt/control.
   * @returns The coerced boolean value when applicable; otherwise the original `storedValue`.
   */
  private coerceStoredControlValue(
    componentType: ComponentType | undefined,
    storedValue: StoredPromptValue
  ): StoredPromptValue {
    const isBooleanInputComponent =
      componentType === ComponentType.TermsAndConditions
      || componentType === ComponentType.CheckboxFormField
      || componentType === ComponentType.RadioFormField;

    if (!isBooleanInputComponent || typeof storedValue !== 'string') {
      return storedValue;
    }

    const normalized = storedValue.trim().toLowerCase();
    if (normalized === 'true') {
      return true;
    }
    if (normalized === 'false') {
      return false;
    }

    return storedValue;
  }

  /**
   * Retrieves the stored value for a given input ID from the prompt data dictionary.
   * If the input ID is not found, returns the provided default value.
   *
   * @param inputId - The ID of the input for which to retrieve the stored value.
   * @param defaultValue - The default value to return if the input ID is not found.
   * @returns The stored value for the input ID, or the default value if not found.
   */
  private returnFormInputValue(
    inputId: string,
    defaultValue?: string | number | boolean
  ): StoredPromptValue {
    const promptDataDictionary = (this.promptData() as NextPrompt).promptDataDictionary;
    const hasInputValue = !!promptDataDictionary
      && Object.hasOwn(promptDataDictionary, inputId);

    if (hasInputValue) {
      return promptDataDictionary?.[inputId]?.value;
    }

    return defaultValue;
  }

  private getPromptData() {
    this.spinnerService.showSpinner();
    this.promptService.getNewEnrollment(this.programToken()).subscribe({
      next: (response) => {
        this.spinnerService.hideSpinner();
        if (response.nextPrompt) {
          response.nextPrompt.promptDataDictionary = response.promptDataDictionary!;
        }
        this.setPrompt(response.nextPrompt);
        this.sessionId.set(response.sessionId);
      },
      error: (error: HttpErrorResponse) => {
        this.spinnerService.hideSpinner();
        this.toastMessageService.showToast(ToastMessageType.Error, errorToastValue(error));
      }
    });
  }

  private loadExistingSession(sessionId: string): void {
    this.spinnerService.showSpinner();
    this.promptService.getEnrollmentSession(sessionId).subscribe({
      next: (response) => {
        this.sessionId.set(response.sessionId);
        response.nextPrompt.promptDataDictionary = response.promptDataDictionary;

        // Resuming straight onto the review step needs the aggregated answers too. Only next()
        // used to fetch them, so a refresh or a deep link there rendered an empty review.
        if (this.hasReviewComponent(response.nextPrompt.components)) {
          this.promptService.getReviewPrompt(response.sessionId).subscribe({
            next: (reviewResponse) => {
              this.setReview(reviewResponse);
              this.setPrompt(response.nextPrompt);
              this.spinnerService.hideSpinner();
            },
            error: (error: HttpErrorResponse) => {
              this.spinnerService.hideSpinner();
              this.toastMessageService.showToast(ToastMessageType.Error, errorToastValue(error));
            }
          });
          return;
        }

        this.spinnerService.hideSpinner();
        this.setPrompt(response.nextPrompt);
      },
      error: (error: HttpErrorResponse) => {
        this.spinnerService.hideSpinner();
        this.toastMessageService.showToast(ToastMessageType.Error, errorToastValue(error));
      }
    });
  }

  // A prompt is the review/submit step iff it contains a review component. This is derived from the
  // component type, never the key path: a prompt can be named/keyed "review" (e.g. household/review)
  // while actually carrying a form component, and must not be treated as the terminal review.
  private hasReviewComponent(components: PromptComponent[] | undefined): boolean {
    return components?.some((component) => component.componentType === ComponentType.Review) ?? false;
  }

  private subscribeFormGroup(): void {
    const formGroups = Object.values(this.formGroups);
    const isReview = this.hasReviewComponent(this.promptData().components);
    this.isReviewPrompt.set(isReview);

    // Uniquified components each get their own FormGroup. Optional radios stay pristine when
    // skipped, so requiring every group to be dirty blocked Next. Enable Next when every group
    // is valid (mandatory radios still fail atLeastOneSelected until a choice is made).
    if (formGroups.length === 0) {
      this.isPromptValid.set(isReview);
      return;
    }

    combineLatest(formGroups.map((form) => form.statusChanges.pipe(startWith(form.status))))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((statuses) => {
        this.isReviewPrompt.set(isReview);
        this.isPromptValid.set(isReview || statuses.every((status) => status === 'VALID'));
      });
  }
}
